import { useState, useEffect, useCallback } from "react";
import { X, Calendar, FileText, Users, TrendingUp, AlertCircle, RefreshCw, Mail, CheckCircle, XCircle } from "lucide-react";
import { getBulkLogMails, trySendBulkLogMails } from "../../../../../services/quotasService";
import { PeriodSelector } from '../../../../common/PeriodsSelector';
import { useAvailableYears } from '../../../../../hooks/useAvailableYears';
import { usePeriodLogic } from '../../../../../hooks/usePeriodLogic';
import Swal from "sweetalert2";
import { ActionLoader } from "../../../../loader";

const MonthlyModal = ({ onClose }) => {
  // Estados del formulario (reemplazando selectedMonth)
  const [formState, setFormState] = useState({
    selectedYear: '',
    reportType: '', 
    selectedPeriod: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [error, setError] = useState("");
  const [showFailedCompanies, setShowFailedCompanies] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Nuevo estado para manejar las respuestas de reintento
  const [retryResult, setRetryResult] = useState(null);
  const [showRetryResult, setShowRetryResult] = useState(false);

  // Hooks para el selector de períodos
  const { availableYears, isLoading: yearsLoading, error: yearsError } = useAvailableYears();
  const { getApiPeriodName } = usePeriodLogic();

  // Manejar cambios del selector
  const handleFormChange = useCallback((field, value) => {
    setFormState(prev => {
      const newState = { ...prev, [field]: value };
      
      if (field === 'selectedYear') {
        newState.reportType = '';
        newState.selectedPeriod = '';
      } else if (field === 'reportType') {
        newState.selectedPeriod = '';
      }
      
      return newState;
    });
    setReport(null);
    setError(''); 
    // Limpiar resultados anteriores del reintento
    setRetryResult(null);
    setShowRetryResult(false);
  }, []);

  // Cerrar con ESC
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const handleLoadReport = async () => {
    const { selectedYear, reportType, selectedPeriod } = formState;
    
    if (!selectedYear || !reportType || !selectedPeriod) {
      setError("Por favor completa todos los campos");
      return;
    }

    setLoading(true);
    setError("");
    setReport(null);
    // Limpiar resultados anteriores del reintento
    setRetryResult(null);
    setShowRetryResult(false);

    try {
      const periodName = getApiPeriodName(reportType, selectedPeriod);
      const response = await getBulkLogMails(periodName, selectedYear);
      
      if (response.success && response.result && response.result.length > 0) {
        setReport(response.result[0]);
      } else {
        setError("No se encontraron datos para el periodo seleccionado");
      }
    } catch (err) {
      setError(err.message || "Error cargando reporte");
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = async () => {
    const confirmation = await Swal.fire({
      title: 'Confirmar Reintento',
      text: "¿Estás seguro de que deseas reintentar el envío de correos fallidos?",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Sí, reintentar',
      cancelButtonText: 'Cancelar',
      customClass: {
        confirmButton: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500',
        cancelButton: 'bg-gray-300 text-gray-800 hover:bg-gray-400 focus:ring-gray-500',
      },
    });
    
    if (!confirmation.isConfirmed) return;
    
    try {
      setIsSubmitting(true);
      setError(""); // Limpiar errores previos
      
      const { selectedYear, reportType, selectedPeriod } = formState;
      const periodName = getApiPeriodName(reportType, selectedPeriod);
      const response = await trySendBulkLogMails(periodName, selectedYear);

      if (response?.success && response.result) {
        const { totalEmpresas, reenviados, fallidos } = response.result;
        
        // Guardar el resultado del reintento
        setRetryResult({
          totalEmpresas,
          reenviados,
          fallidos,
          success: fallidos === 0,
          timestamp: new Date().toLocaleString('es-ES')
        });
        
        setShowRetryResult(true);
        
        // Mostrar notificación success/warning según el resultado
        if (fallidos === 0) {
          const confirm = await Swal.fire({
            title: '¡Éxito!',
            text: `Todos los correos se reenviaron exitosamente (${reenviados} de ${totalEmpresas})`,
            icon: 'success',
            confirmButtonText: 'Entendido',
            customClass: {
              confirmButton: 'bg-green-600 text-white hover:bg-green-700'
            }
          });
          if (confirm.isConfirmed) handleLoadReport(); // Recargar reporte si se confirma
        } else if (reenviados === 0) {
          const confirm = await Swal.fire({
            title: 'Reintento Fallido',
            text: `No se pudieron reenviar correos de un total de ${totalEmpresas}`,
            icon: 'error',
            confirmButtonText: 'Entendido',
            customClass: {
              confirmButton: 'bg-red-600 text-white hover:bg-red-700'
            }
          });
          if (confirm.isConfirmed) handleLoadReport(); // Recargar reporte si se confirma
        } else {
          const confirm = await Swal.fire({
            title: 'Reintento Parcial',
            text: `Se reenviaron ${reenviados} correos, pero ${fallidos} fallaron.`,
            icon: 'warning',
            confirmButtonText: 'Entendido',
            customClass: {
              confirmButton: 'bg-yellow-600 text-white hover:bg-yellow-700'
            }
          });
          if (confirm.isConfirmed) handleLoadReport(); // Recargar reporte si se confirma
        }

      } else {
        setError("❌ Error inesperado al reintentar el envío.");
        Swal.fire({
          title: 'Error',
          text: 'No se pudo completar el reintento de envío',
          icon: 'error',
          confirmButtonText: 'Entendido'
        });
      }
      
    } catch (err) {
      console.error("Error en reintento:", err);
      setError("❌ Error al reintentar el envío de correos.");
      Swal.fire({
        title: 'Error',
        text: 'Ocurrió un error al reintentar el envío de correos',
        icon: 'error',
        confirmButtonText: 'Entendido'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getFailedCompaniesList = () => {
    if (!report?.failedCompanies) return [];
    return report.failedCompanies.split(', ').filter(company => company.trim() !== '');
  };

  const getNotFoundMailList = () => {
    if (!report?.notFoundMail) return [];
    return report.notFoundMail.split(', ').filter(company => company.trim() !== '');
  };

  const failedCompaniesList = getFailedCompaniesList();
  const notFoundMailList = getNotFoundMailList();

  // Verificar si el formulario está completo
  const isFormComplete = formState.selectedYear && formState.reportType && formState.selectedPeriod;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
      <ActionLoader open={isSubmitting} onClose={() => setIsSubmitting(false)}></ActionLoader>
      <div 
        className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden shadow-2xl transform transition-all duration-300 ease-out"
        style={{
          animation: 'modalSlideIn 0.3s ease-out'
        }}
      >
        {/* Header del Modal */}
        <div className="bg-gradient-to-r from-[#0488AF] to-[#09274A] p-6 text-white">
          <div className="flex justify-between items-start">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white bg-opacity-20 rounded-lg">
                <FileText className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Reporte de Correos Masivos</h2>
                <p className="text-blue-100 text-sm mt-1">Análisis por período</p>
              </div>
            </div>
            <button 
              onClick={onClose} 
              className="text-white hover:text-red-200 hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-all duration-200 transform hover:scale-110"
              title="Cerrar (ESC)"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Contenido del Modal */}
        <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 120px)' }}>
          <div className="space-y-6">
            
            {/* Selector de Período */}
            <div className="bg-gray-50 p-4 rounded-xl border-l-4 border-[#0488AF]">
              <div className="flex items-center gap-2 mb-4">
                <Calendar className="w-5 h-5 text-[#0488AF]" />
                <label className="text-sm font-semibold text-[#09274A] uppercase tracking-wide">
                  Seleccionar Período
                </label>
              </div>
              
              {yearsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-[#0488AF]" />
                  <span className="ml-2 text-gray-600">Cargando años disponibles...</span>
                </div>
              ) : (
                <>
                  <PeriodSelector
                    availableYears={availableYears}
                    selectedYear={formState.selectedYear}
                    reportType={formState.reportType}
                    selectedPeriod={formState.selectedPeriod}
                    onYearChange={(year) => handleFormChange('selectedYear', year)}
                    onReportTypeChange={(type) => handleFormChange('reportType', type)}
                    onPeriodChange={(period) => handleFormChange('selectedPeriod', period)}
                    disabled={loading}
                  />

                  <div className="flex justify-end mt-4">
                    <button
                      onClick={handleLoadReport}
                      disabled={loading || !isFormComplete}
                      className="px-6 py-3 bg-[#0488AF] text-white rounded-lg hover:bg-[#0488AF]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 transform hover:scale-105 flex items-center gap-2"
                    >
                      {loading ? (
                        <>
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          Cargando...
                        </>
                      ) : (
                        <>
                          <TrendingUp className="w-4 h-4" />
                          Cargar Reporte
                        </>
                      )}
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Resultado del Reintento */}
            {showRetryResult && retryResult && (
              <div className={`p-4 rounded-xl border-l-4 ${
                retryResult.success 
                  ? 'bg-green-50 border-green-500' 
                  : 'bg-yellow-50 border-yellow-500'
              }`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    {retryResult.success ? (
                      <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                    ) : (
                      <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0" />
                    )}
                    <div>
                      <h4 className={`font-semibold ${
                        retryResult.success ? 'text-green-800' : 'text-yellow-800'
                      }`}>
                        Resultado del Reintento
                      </h4>
                      <div className={`text-sm mt-1 ${
                        retryResult.success ? 'text-green-700' : 'text-yellow-700'
                      }`}>
                        <div className="grid grid-cols-3 gap-4 mt-2">
                          <div className="text-center">
                            <div className="font-bold text-lg">{retryResult.totalEmpresas}</div>
                            <div className="text-xs">Total</div>
                          </div>
                          <div className="text-center">
                            <div className="font-bold text-lg text-green-600">{retryResult.reenviados}</div>
                            <div className="text-xs">Enviados</div>
                          </div>
                          <div className="text-center">
                            <div className="font-bold text-lg text-red-600">{retryResult.fallidos}</div>
                            <div className="text-xs">Fallidos</div>
                          </div>
                        </div>
                        <div className="text-xs mt-2 opacity-75">
                          Procesado: {retryResult.timestamp}
                        </div>
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowRetryResult(false)}
                    className="text-gray-400 hover:text-gray-600 p-1 hover:bg-white rounded-full transition-colors duration-200"
                    title="Ocultar"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Error/Success Message */}
            {error && (
              <div className={`p-4 rounded-xl border-l-4 ${
                error.includes('exitosamente') 
                  ? 'bg-green-50 border-green-500 text-green-800' 
                  : 'bg-red-50 border-red-500 text-red-800'
              }`}>
                <div className="flex items-center gap-2">
                  {error.includes('exitosamente') ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-red-600" />
                  )}
                  <span className="font-medium">{error}</span>
                </div>
              </div>
            )}

            {/* Reporte */}
            {report && (
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 p-5 rounded-xl border">
                <div className="border-b border-gray-200 pb-4 mb-6">
                  <h3 className="text-lg font-bold text-[#09274A] flex items-center gap-2">
                    <FileText className="w-5 h-5 text-[#0488AF]" />
                    Resumen del Reporte - {report.month} {report.year}
                  </h3>
                </div>

                {/* Estadísticas principales */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <div className="bg-white p-4 rounded-xl border-l-4 border-[#0488AF] shadow-sm hover:shadow-md transition-shadow duration-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-2xl font-bold text-[#0488AF]">{report.totalCompanies}</div>
                        <div className="text-sm text-gray-600">Total Empresas</div>
                      </div>
                      <Users className="w-8 h-8 text-[#0488AF] opacity-20" />
                    </div>
                  </div>
                  
                  <div className="bg-white p-4 rounded-xl border-l-4 border-green-500 shadow-sm hover:shadow-md transition-shadow duration-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-2xl font-bold text-green-600">{report.withContact}</div>
                        <div className="text-sm text-gray-600">Con Contacto</div>
                      </div>
                      <CheckCircle className="w-8 h-8 text-green-500 opacity-20" />
                    </div>
                  </div>
                  
                  <div className="bg-white p-4 rounded-xl border-l-4 border-yellow-500 shadow-sm hover:shadow-md transition-shadow duration-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-2xl font-bold text-yellow-600">{report.sent}</div>
                        <div className="text-sm text-gray-600">Enviados</div>
                      </div>
                      <Mail className="w-8 h-8 text-yellow-500 opacity-20" />
                    </div>
                  </div>
                  
                  <div className="bg-white p-4 rounded-xl border-l-4 border-red-500 shadow-sm hover:shadow-md transition-shadow duration-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-2xl font-bold text-red-600">{report.failed}</div>
                        <div className="text-sm text-gray-600">Fallidos</div>
                      </div>
                      <XCircle className="w-8 h-8 text-red-500 opacity-20" />
                    </div>
                  </div>
                </div>

                {/* Sin contacto */}
                {report.withoutContact > 0 && (
                  <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <AlertCircle className="w-5 h-5 text-orange-600" />
                        <span className="font-semibold text-orange-800">
                          Sin Contacto: {report.withoutContact} empresas
                        </span>
                      </div>
                      {notFoundMailList.length > 0 && (
                        <button
                          onClick={() => setShowFailedCompanies(!showFailedCompanies)}
                          className="text-orange-600 hover:text-orange-800 text-sm font-medium px-3 py-1 rounded-lg hover:bg-orange-100 transition-colors duration-200"
                        >
                          {showFailedCompanies ? 'Ocultar' : 'Ver'} detalles
                        </button>
                      )}
                    </div>
                    
                    {showFailedCompanies && notFoundMailList.length > 0 && (
                      <div className="mt-4 p-3 bg-orange-100 rounded-lg max-h-32 overflow-y-auto">
                        <div className="text-sm space-y-1">
                          {notFoundMailList.map((company, index) => (
                            <div key={index} className="text-orange-700 flex items-center gap-2">
                              <span className="w-1 h-1 bg-orange-500 rounded-full"></span>
                              {company.trim()}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Empresas fallidas */}
                {report.failed > 0 && failedCompaniesList.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-4">
                      <XCircle className="w-5 h-5 text-red-600" />
                      <h4 className="font-semibold text-red-800">
                        Envíos Fallidos ({report.failed})
                      </h4>
                    </div>
                    
                    <div className="mb-4 p-3 bg-red-100 rounded-lg max-h-32 overflow-y-auto">
                      <div className="text-sm space-y-1">
                        {failedCompaniesList.map((company, index) => (
                          <div key={index} className="text-red-700 flex items-center gap-2">
                            <span className="w-1 h-1 bg-red-500 rounded-full"></span>
                            {company.trim()}
                          </div>
                        ))}
                      </div>
                    </div>

                    {report.errorMessage && (
                      <div className="text-sm text-red-700 mb-4 p-3 bg-red-100 rounded-lg">
                        <strong>Error:</strong> {report.errorMessage}
                      </div>
                    )}

                    <button
                      onClick={handleRetry}
                      disabled={loading || isSubmitting}
                      className="w-full px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all duration-200 transform hover:scale-105"
                    >
                      {isSubmitting ? (
                        <>
                          <RefreshCw className="w-5 h-5 animate-spin" />
                          Procesando...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="w-5 h-5" />
                          Reintentar Envíos Fallidos
                        </>
                      )}
                    </button>
                  </div>
                )}

                {/* Footer con metadatos */}
                {report.createdAt && (
                  <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm mt-6">
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Calendar className="w-4 h-4 text-[#0488AF]" />
                      <span>Reporte generado:</span>
                      <span className="text-[#09274A] font-semibold">
                        {new Date(report.createdAt).toLocaleString('es-ES')}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      
      <style>{`
        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: scale(0.9) translateY(-20px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default MonthlyModal;